import { API_BASE_URL } from './client';

// Data Collections API
/** Legacy single-file upload: creates a new collection and starts embedding in the background. */
export const uploadCollectionFileLegacy = async (keycloak, file, options = {}) => {
  if (!keycloak || !keycloak.token) throw new Error('Not authenticated');
  const formData = new FormData();
  formData.append('file', file);
  if (options.group_id != null) formData.append('group_id', String(options.group_id));
  const response = await fetch(`${API_BASE_URL}/data-collections/upload/`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${keycloak.token}` },
    body: formData,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || 'Upload failed');
  }
  return response.json();
};

export const getDataCollections = async (keycloak, showAll = false) => {
  try {
    const params = new URLSearchParams();
    if (showAll) params.append('show_all', 'true');
    const response = await fetch(`${API_BASE_URL}/data-collections/collections/?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${keycloak.token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching data collections:', error);
    throw error;
  }
};

export const createDataCollection = async (keycloak, formData) => {
  if (!keycloak || !keycloak.token) throw new Error('Not authenticated');
  const response = await fetch(`${API_BASE_URL}/data-collections/collections/`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${keycloak.token}` },
    body: formData,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to create collection');
  }
  return response.json();
};

export const getDataCollection = async (keycloak, collectionId) => {
  if (!keycloak || !keycloak.token) throw new Error('Not authenticated');
  const response = await fetch(`${API_BASE_URL}/data-collections/collections/${collectionId}`, {
    headers: { 'Authorization': `Bearer ${keycloak.token}` },
  });
  if (!response.ok) throw new Error('Failed to fetch collection');
  return response.json();
};

export const updateDataCollection = async (keycloak, collectionId, formData) => {
  if (!keycloak || !keycloak.token) throw new Error('Not authenticated');
  const response = await fetch(`${API_BASE_URL}/data-collections/collections/${collectionId}`, {
    method: 'PUT',
    headers: { 'Authorization': `Bearer ${keycloak.token}` },
    body: formData,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to update collection');
  }
  return response.json();
};

export const deleteDataCollection = async (keycloak, collectionId) => {
  if (!keycloak || !keycloak.token) throw new Error('Not authenticated');
  const response = await fetch(`${API_BASE_URL}/data-collections/collections/${collectionId}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${keycloak.token}` },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to delete collection');
  }
  return response.json();
};

export const getCollectionFiles = async (keycloak, collectionId) => {
  if (!keycloak || !keycloak.token) throw new Error('Not authenticated');
  const response = await fetch(`${API_BASE_URL}/data-collections/collections/${collectionId}/files/`, {
    headers: { 'Authorization': `Bearer ${keycloak.token}` },
  });
  if (!response.ok) throw new Error('Failed to fetch collection files');
  return response.json();
};

export const uploadFileToCollection = async (keycloak, collectionId, formData) => {
  if (!keycloak || !keycloak.token) throw new Error('Not authenticated');
  const response = await fetch(`${API_BASE_URL}/data-collections/collections/${collectionId}/files/`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${keycloak.token}` },
    body: formData,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to upload file');
  }
  return response.json();
};

export const deleteCollectionFile = async (keycloak, collectionId, fileId) => {
  if (!keycloak || !keycloak.token) throw new Error('Not authenticated');
  const response = await fetch(`${API_BASE_URL}/data-collections/collections/${collectionId}/files/${fileId}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${keycloak.token}` },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to delete file');
  }
  return response.json();
};

export const downloadCollectionFile = async (keycloak, collectionId, fileId, filename) => {
  if (!keycloak || !keycloak.token) throw new Error('Not authenticated');
  const response = await fetch(`${API_BASE_URL}/data-collections/collections/${collectionId}/files/${fileId}/download`, {
    headers: { 'Authorization': `Bearer ${keycloak.token}` },
  });
  if (!response.ok) throw new Error('Failed to download file');
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'download';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  window.setTimeout(() => {
    window.URL.revokeObjectURL(url);
    a.remove();
  }, 1500);
};

export const previewCollectionFile = async (keycloak, collectionId, fileId, offset = 0, limit = 10) => {
  if (!keycloak || !keycloak.token) throw new Error('Not authenticated');
  const params = new URLSearchParams({ offset: String(offset), limit: String(limit) });
  const response = await fetch(`${API_BASE_URL}/data-collections/collections/${collectionId}/files/${fileId}/preview?${params.toString()}`, {
    headers: { 'Authorization': `Bearer ${keycloak.token}` },
  });
  if (!response.ok) throw new Error('Failed to preview file');
  return response.json();
};

export const getCollectionEmbeddingStatus = async (keycloak, collectionId) => {
  if (!keycloak || !keycloak.token) throw new Error('Not authenticated');
  const response = await fetch(`${API_BASE_URL}/data-collections/collections/${collectionId}/embedding-status`, {
    headers: { 'Authorization': `Bearer ${keycloak.token}` },
  });
  if (!response.ok) throw new Error('Failed to fetch embedding status');
  return response.json();
};

// Web Page Sources
export const addWebPageToCollection = async (keycloak, collectionId, payload) => {
  if (!keycloak || !keycloak.token) throw new Error('Not authenticated');
  const response = await fetch(`${API_BASE_URL}/data-collections/collections/${collectionId}/webpages/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${keycloak.token}`,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to add web page');
  }
  return response.json();
};

export const refreshWebPage = async (keycloak, collectionId, fileId, payload = {}) => {
  if (!keycloak || !keycloak.token) throw new Error('Not authenticated');
  const response = await fetch(`${API_BASE_URL}/data-collections/collections/${collectionId}/webpages/${fileId}/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${keycloak.token}`,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to refresh web page');
  }
  return response.json();
};

// Collection Version History
export const getCollectionVersions = async (keycloak, collectionId, limit = 50, offset = 0) => {
  if (!keycloak || !keycloak.token) throw new Error('Not authenticated');
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  const response = await fetch(`${API_BASE_URL}/data-collections/collections/${collectionId}/versions/?${params.toString()}`, {
    headers: { 'Authorization': `Bearer ${keycloak.token}` },
  });
  if (!response.ok) throw new Error('Failed to fetch collection versions');
  return response.json();
};

// PageIndex tree structure
export const getPageIndexStructure = async (keycloak, collectionId) => {
  if (!keycloak || !keycloak.token) throw new Error('Not authenticated');
  const response = await fetch(`${API_BASE_URL}/data-collections/collections/${collectionId}/pageindex-structure`, {
    headers: { 'Authorization': `Bearer ${keycloak.token}` },
  });
  if (!response.ok) throw new Error('Failed to fetch PageIndex structure');
  return response.json();
};
